import { SimpleUnchangedProps, addEffect, flowMax } from 'ad-hok'

import { Api, addShopperUser } from '../api'
import {
  OutgoingProductClickInput,
  ProductDiscoverySource,
  SearchResultClick,
} from '../api/types'
import { LoginTrigger } from './auth'

export type Category = 'media' | 'edit' | 'product' | 'profile' | 'general'

export type Action =
  | 'bookmark'
  | 'share'
  | 'shop'
  | 'show_rodeo'
  | 'media_view'
  | 'product_view'
  | 'navigate'
  | 'site_visit'
  | 'follow'
  | 'show_behind_the_gate'
  | 'user_list_view'
  | 'search'
  | 'search_result_click'
  | 'join_creator_waitlist_from_notification'
  | 'join_creator_waitlist_notification_time_out'
  | 'join_app_waitlist_from_notification'
  | 'join_app_waitlist_notification_time_out'
  | 'banner_open_download_in_app_click'
  | 'banner_open_download_in_app_close'
  | 'banner_open_download_in_app_appstore'
  | 'login'
  | 'show_more_products'
  | 'featured_brand_click'

export interface Options {
  category: Category
  action: Action
}

function isBot() {
  const botUserAgents = [
    /bot/i,
    /crawler/i,
    /spider/i,
    /crawling/i,
    /googlebot/i,
    /bingbot/i,
    /yandex/i,
    /baiduspider/i,
    /duckduckbot/i,
    /slurp/i,
    /facebookexternalhit/i,
    /twitterbot/i,
    /linkedinbot/i,
  ]

  const ua = navigator.userAgent
  return botUserAgents.some((pattern) => pattern.test(ua))
}

const gtag: any = (window as any).gtag

export const trackEvent = (options: Options, params?: Record<string, any>) => {
  if (isBot()) {
    return
  }
  gtag('event', options.action, {
    category: options.category,
    ...(params ?? {}),
  })
  let bannerCountStr = sessionStorage.getItem('bannerCount')
  if (bannerCountStr) {
    sessionStorage.setItem('bannerCount', `${parseInt(bannerCountStr) + 1}`)
  }
}

export const setAnalyticsUserIdOnChange: SimpleUnchangedProps = flowMax(
  addShopperUser,
  addEffect(
    ({ user }) =>
      () => {
        gtag('set', {
          user_id: user?.id,
        })
      },
    ['user']
  )
)

export const trackMediaView = (
  mediaId: number,
  creatorId: number,
  api: Api
) => {
  trackEvent(
    {
      category: 'media',
      action: 'media_view',
    },
    {
      media_id: mediaId,
      creator_id: creatorId,
    }
  )

  api.analytics(isBot()).createMediaView({ mediaId })
}

type OutgoingClickOptions = {
  url: string
  creatorId: number
}

export const trackOutgoingClick = (
  { url, creatorId }: OutgoingClickOptions,
  api: Api
) => {
  api.analytics(isBot()).createOutgoingClick({
    creatorId,
    url,
  })
}

interface ProductViewOptions {
  productId: number
  mediaId?: number
  userListId?: number
  creatorId?: number
  discoverySource?: ProductDiscoverySource
}

export const trackProductView = (
  {
    productId,
    mediaId,
    userListId,
    creatorId,
    discoverySource,
  }: ProductViewOptions,
  api: Api
) => {
  trackEvent(
    {
      category: 'product',
      action: 'product_view',
    },
    {
      product_id: productId,
      media_id: mediaId,
      user_list_id: userListId,
      creator_id: creatorId,
      discovery_source: discoverySource,
    }
  )

  api.analytics(isBot()).createProductView({
    productId,
    mediaId,
    userListId,
    discoverySource,
  })
}

export const trackBehindTheGateView = (creatorId: number, api: Api) => {
  api.analytics(isBot()).createBehindTheGateView({ creatorId })
}

export const trackUserListView = (userListId: number, api: Api) => {
  trackEvent(
    {
      category: 'edit',
      action: 'user_list_view',
    },
    {
      user_list_id: userListId,
    }
  )
  api.analytics(isBot()).createUserListView({ userListId })
}

export const trackEditFeedView = (editId: number, api: Api) => {
  api.analytics(isBot()).createEditFeedView({ editId })
}

export const trackLoginTrigger = (loginTrigger: LoginTrigger) => {
  trackEvent(
    {
      category: 'general',
      action: 'login',
    },
    {
      login_trigger: loginTrigger,
    }
  )
}

export const trackFeaturedBrandClick = (brandId: number) => {
  trackEvent(
    {
      category: 'general',
      action: 'featured_brand_click',
    },
    {
      brand_id: brandId,
    }
  )
}

export const trackSeachResultClick = (input: SearchResultClick, api: Api) => {
  api.analytics(isBot()).createSearchResultClick({ result: input })
}

export const trackProductOutgoingClick = (
  input: OutgoingProductClickInput,
  user_id: number | undefined,
  api: Api
) => {
  const createOutgoingProductClickPromise = api
    .analytics(isBot())
    .createOutgoingProductClick(input)
    .catch((error) =>
      console.error('Error creating outgoing product click', error)
    )

  trackEvent(
    {
      category: 'product',
      action: 'shop',
    },
    {
      media_id: input.mediaId,
      user_list_id: input.userListId,
      product_id: input.productId,
      creator_id: user_id,
    }
  )
  return createOutgoingProductClickPromise
}
